import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as gifuct from '../../vendor/gifuct-js/index.js';
import { GIFEncoder, quantize, applyPalette } from '../../vendor/gifenc/gifenc.esm.js';
import { encodeCanvasToImageFile } from '../../utils/mediaCompression';

/**
 * Reusable square avatar/logo cropper — a React port of the IdP's
 * assets/js/avatar_crop.js, shared by the channel avatar (NewChannelModal) and
 * the team logo (TeamsModal) uploads.
 *
 * A Canvas 2D pan/zoom stage lets a non-square image be positioned instead of
 * being squished by a `border-radius`. Static images export as a 512² PNG.
 * Animated GIFs are decoded frame-by-frame (honoring each frame's disposal
 * method), the same crop applied to every frame, then re-encoded as a new
 * animated GIF (a plain canvas snapshot would flatten it to one frame).
 *
 * Props:
 *   file       File to crop (component renders only when non-null)
 *   title      modal heading
 *   saveLabel  primary button label ("Save photo")
 *   onCancel   () => void
 *   onCropped  (croppedFile: File) => void   — image/png or image/gif
 */
const STAGE = 320;
const STATIC_OUTPUT = 512;
const GIF_OUTPUT = 256;
const GIF_MAX_FRAMES = 300;

// Composite gifuct-js's per-frame patches into full logical-screen frames,
// honoring GIF disposal methods (0/1 leave, 2 restore-to-bg, 3 restore-prev).
function buildGifFrames(parsedGif) {
  const frames = gifuct.decompressFrames(parsedGif, true);
  const width = parsedGif.lsd.width;
  const height = parsedGif.lsd.height;

  const full = document.createElement('canvas');
  full.width = width;
  full.height = height;
  const fctx = full.getContext('2d');

  const out = [];
  frames.forEach((frame) => {
    const { dims, patch, delay, disposalType } = frame;

    let restoreData = null;
    if (disposalType === 3) restoreData = fctx.getImageData(0, 0, width, height);

    const patchCanvas = document.createElement('canvas');
    patchCanvas.width = dims.width;
    patchCanvas.height = dims.height;
    patchCanvas.getContext('2d').putImageData(new ImageData(patch, dims.width, dims.height), 0, 0);
    fctx.drawImage(patchCanvas, dims.left, dims.top);

    const frameCanvas = document.createElement('canvas');
    frameCanvas.width = width;
    frameCanvas.height = height;
    frameCanvas.getContext('2d').drawImage(full, 0, 0);
    out.push({ canvas: frameCanvas, delay: delay || 100 });

    if (disposalType === 2) {
      fctx.clearRect(dims.left, dims.top, dims.width, dims.height);
    } else if (disposalType === 3 && restoreData) {
      fctx.putImageData(restoreData, 0, 0);
    }
  });

  return { width, height, frames: out };
}

export default function ImageCropper({ file, title = 'Adjust image', saveLabel = 'Save', onCancel, onCropped }) {
  const canvasRef = useRef(null);
  // All the imperative crop state lives in a ref so pan/zoom don't churn React.
  const s = useRef({
    img: null,
    mode: 'static',
    gifFrames: null,
    gifTimer: null,
    gifIndex: 0,
    minScale: 1,
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    dragging: false,
    lastX: 0,
    lastY: 0,
  });
  const [zoom, setZoom] = useState(1);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const draw = useCallback(() => {
    const c = canvasRef.current;
    const st = s.current;
    if (!c || !st.img) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, STAGE, STAGE);
    ctx.drawImage(st.img, st.offsetX, st.offsetY, st.img.width * st.scale, st.img.height * st.scale);
  }, []);

  const clampOffsets = useCallback(() => {
    const st = s.current;
    const w = st.img.width * st.scale;
    const h = st.img.height * st.scale;
    st.offsetX = Math.min(0, Math.max(STAGE - w, st.offsetX));
    st.offsetY = Math.min(0, Math.max(STAGE - h, st.offsetY));
  }, []);

  const initTransform = useCallback((w, h) => {
    const st = s.current;
    st.minScale = Math.max(STAGE / w, STAGE / h);
    st.scale = st.minScale;
    st.offsetX = (STAGE - w * st.scale) / 2;
    st.offsetY = (STAGE - h * st.scale) / 2;
    setZoom(1);
  }, []);

  const applyZoom = useCallback(
    (value) => {
      const st = s.current;
      const v = Math.min(3, Math.max(1, value));
      const old = st.scale;
      st.scale = st.minScale * v;
      const cx = STAGE / 2;
      const cy = STAGE / 2;
      const ix = (cx - st.offsetX) / old;
      const iy = (cy - st.offsetY) / old;
      st.offsetX = cx - ix * st.scale;
      st.offsetY = cy - iy * st.scale;
      clampOffsets();
      setZoom(v);
      draw();
    },
    [clampOffsets, draw]
  );

  const stopGifPreview = useCallback(() => {
    if (s.current.gifTimer) {
      clearTimeout(s.current.gifTimer);
      s.current.gifTimer = null;
    }
  }, []);

  const startGifPreview = useCallback(() => {
    const st = s.current;
    st.gifIndex = 0;
    const showFrame = () => {
      if (st.mode !== 'gif' || !st.gifFrames) return;
      const frame = st.gifFrames[st.gifIndex];
      st.img = frame.canvas;
      draw();
      st.gifTimer = window.setTimeout(() => {
        st.gifIndex = (st.gifIndex + 1) % st.gifFrames.length;
        showFrame();
      }, Math.max(20, frame.delay));
    };
    showFrame();
  }, [draw]);

  // Load the selected file into the stage whenever it changes.
  useEffect(() => {
    if (!file) return undefined;
    const st = s.current;
    setError(null);
    setReady(false);
    stopGifPreview();

    const loadStatic = (dataUrl) => {
      const image = new Image();
      image.onload = () => {
        st.mode = 'static';
        st.gifFrames = null;
        st.img = image;
        initTransform(image.width, image.height);
        setReady(true);
        draw();
      };
      image.onerror = () => setError("That file doesn't look like a valid image.");
      image.src = dataUrl;
    };

    const loadGif = (arrayBuffer) => {
      let built;
      try {
        built = buildGifFrames(gifuct.parseGIF(arrayBuffer));
      } catch {
        setError('Could not read that GIF.');
        return;
      }
      if (!built.frames.length) {
        setError('That GIF has no frames.');
        return;
      }
      if (built.frames.length === 1) {
        st.mode = 'static';
        st.gifFrames = null;
        st.img = built.frames[0].canvas;
        initTransform(st.img.width, st.img.height);
      } else {
        st.mode = 'gif';
        st.gifFrames = built.frames;
        st.img = built.frames[0].canvas;
        initTransform(built.width, built.height);
      }
      setReady(true);
      draw();
      if (st.mode === 'gif') startGifPreview();
    };

    const reader = new FileReader();
    if (file.type === 'image/gif') {
      reader.onload = () => loadGif(reader.result);
      reader.onerror = () => setError('Could not read that file.');
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = () => loadStatic(reader.result);
      reader.onerror = () => setError('Could not read that file.');
      reader.readAsDataURL(file);
    }

    return () => {
      stopGifPreview();
      st.img = null;
      st.gifFrames = null;
    };
  }, [file, draw, initTransform, startGifPreview, stopGifPreview]);

  const onPointerDown = (e) => {
    const st = s.current;
    if (!st.img) return;
    st.dragging = true;
    st.lastX = e.clientX;
    st.lastY = e.clientY;
    canvasRef.current.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const st = s.current;
    if (!st.dragging || !st.img) return;
    st.offsetX += e.clientX - st.lastX;
    st.offsetY += e.clientY - st.lastY;
    st.lastX = e.clientX;
    st.lastY = e.clientY;
    clampOffsets();
    draw();
  };
  const endDrag = () => {
    s.current.dragging = false;
  };
  const onWheel = (e) => {
    if (!s.current.img) return;
    e.preventDefault();
    applyZoom(zoom + (e.deltaY > 0 ? -0.06 : 0.06));
  };

  const fail = useCallback((msg) => {
    setBusy(false);
    setError(msg);
  }, []);

  const saveStatic = useCallback(async () => {
    const st = s.current;
    const out = document.createElement('canvas');
    out.width = STATIC_OUTPUT;
    out.height = STATIC_OUTPUT;
    const octx = out.getContext('2d');
    const factor = STATIC_OUTPUT / STAGE;
    octx.drawImage(
      st.img,
      st.offsetX * factor,
      st.offsetY * factor,
      st.img.width * st.scale * factor,
      st.img.height * st.scale * factor
    );
    try {
      // Reuse the chat pipeline's encoder (WebP→PNG/JPEG) so avatars are small.
      const file = await encodeCanvasToImageFile(out, 'avatar');
      onCropped?.(file);
    } catch {
      fail('Could not process that image — try a different file.');
    }
  }, [onCropped, fail]);

  const saveGif = useCallback(() => {
    const st = s.current;
    const factor = GIF_OUTPUT / STAGE;
    const frames = st.gifFrames.slice(0, GIF_MAX_FRAMES);

    const tmp = document.createElement('canvas');
    tmp.width = GIF_OUTPUT;
    tmp.height = GIF_OUTPUT;
    const tctx = tmp.getContext('2d');
    const gif = GIFEncoder();

    frames.forEach((frame, i) => {
      tctx.clearRect(0, 0, GIF_OUTPUT, GIF_OUTPUT);
      tctx.drawImage(
        frame.canvas,
        st.offsetX * factor,
        st.offsetY * factor,
        frame.canvas.width * st.scale * factor,
        frame.canvas.height * st.scale * factor
      );
      const { data } = tctx.getImageData(0, 0, GIF_OUTPUT, GIF_OUTPUT);
      const palette = quantize(data, 256, { format: 'rgba4444', oneBitAlpha: true });
      const index = applyPalette(data, palette, 'rgba4444');
      const transparentIndex = palette.findIndex((c) => c[3] === 0);
      const opts = {
        palette,
        delay: frame.delay,
        transparent: transparentIndex !== -1,
        transparentIndex: transparentIndex !== -1 ? transparentIndex : 0,
      };
      if (i === 0) opts.repeat = 0;
      gif.writeFrame(index, GIF_OUTPUT, GIF_OUTPUT, opts);
    });

    gif.finish();
    // GIF is already compressed here: cropped to 256² and palette-quantized to
    // ≤256 colors, so it skips the still-image encoder (which flattens GIFs).
    onCropped?.(new File([gif.bytes()], 'avatar.gif', { type: 'image/gif' }));
  }, [onCropped]);

  const onSave = () => {
    if (!s.current.img || busy) return;
    setBusy(true);
    setError(null);
    if (s.current.mode === 'gif') {
      stopGifPreview();
      // Let "Processing…" paint before the synchronous encode.
      requestAnimationFrame(() =>
        setTimeout(() => {
          try {
            saveGif();
          } catch {
            setBusy(false);
            setError('Could not process that GIF — try a shorter clip or a different file.');
          }
        }, 0)
      );
    } else {
      saveStatic();
    }
  };

  if (!file) return null;

  return (
    <div className="img-cropper-scrim" role="dialog" aria-modal="true" aria-label={title}>
      <div className="img-cropper-panel">
        <div className="img-cropper-title">{title}</div>
        <div className="img-cropper-stage">
          <canvas
            ref={canvasRef}
            width={STAGE}
            height={STAGE}
            className="img-cropper-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={endDrag}
            onWheel={onWheel}
          />
          <div className="img-cropper-mask" aria-hidden />
        </div>
        <input
          type="range"
          min="1"
          max="3"
          step="0.01"
          value={zoom}
          disabled={!ready}
          onChange={(e) => applyZoom(parseFloat(e.target.value))}
          className="img-cropper-zoom"
          aria-label="Zoom"
        />
        {error && <div className="img-cropper-error">{error}</div>}
        <div className="img-cropper-actions">
          <button type="button" className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
            disabled={!ready || busy}
            onClick={onSave}
          >
            {busy ? 'Processing…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
